import styles from "./ProtectPlusCompliance.module.scss";
import { ProtectPlusScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import React, { useEffect, useRef, useState } from "react";
import { TickOutlined as Tick } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { InfoOutlined as Info } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InfoOutlined";

import { TransformedExtendedWarrantyMulti } from "@/types/serviceTypes";
import { OpenInNewOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/OpenInNewOutlined";
import { HorizontalRule } from "@/components";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import {
  PrimaryButton,
  SecondaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import CheckboxWithContainer from "@JohnLewisPartnership/jl-design-system/dist/components/checkbox/CheckboxWithContainer";
import { Stepper } from "@JohnLewisPartnership/jl-design-system/dist/components/stepper/Stepper";
import { ProtectPlusSvg } from "@/components/PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg.tsx";
import { replaceVariables } from "@/components/PostAddToBasketDrawer/utils.ts";
import { BasketMode } from "@/components/PostAddToBasketDrawer/components/AvailableServices.tsx";

type ProtectPlusComplianceProps = {
  protectPlusScheme: ProtectPlusScheme;
  guarantees?: { title: string }[];
  warrantyService: TransformedExtendedWarrantyMulti;
  addProtectPlusClicked: (
    s: TransformedExtendedWarrantyMulti,
    q: number,
  ) => Promise<boolean>;
  closeDrawer: () => void;
  maxAllowedServices: number;
  handleStepperChange: (
    s: TransformedExtendedWarrantyMulti,
    q: number,
  ) => Promise<boolean>;
  hideFooter: boolean;
  isDrawerOpen: boolean;
  mode: BasketMode;
};

export const ProtectPlusCompliance = ({
  protectPlusScheme,
  warrantyService,
  addProtectPlusClicked,
  closeDrawer,
  maxAllowedServices,
  handleStepperChange,
  hideFooter,
  isDrawerOpen,
  mode,
}: ProtectPlusComplianceProps) => {
  const duration = warrantyService.duration;
  const marketingMessage = replaceVariables(
    protectPlusScheme.schemeMarketingMessage || "",
    {
      VAR_YEARS: warrantyService.duration,
      VAR_PRICE: "",
    },
  );
  const complaintDetails =
    "Full details on how to complain can be found in the";

  const elementCheckBox = useRef<HTMLDivElement>(null);
  const topOfComponent = useRef<HTMLDivElement>(null);
  const hasInteractedWithTerms = useRef(false);

  const [checked, setChecked] = useState(false);
  const [disableAddProtectPlusButton, setDisableAddProtectPlusButton] =
    useState(true);
  const [termsAgreed, setTermsAgreed] = useState(true);

  const [serviceQuantity, setServiceQuantity] = useState(
    warrantyService.quantity > 0 ? warrantyService.quantity : 1,
  );
  const [protectPlusAdded, setProtectPlusAdded] = useState(
    warrantyService.quantity > 0,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setProtectPlusAdded(warrantyService.quantity > 0);
    setServiceQuantity(
      warrantyService.quantity > 0 ? warrantyService.quantity : 1,
    );
  }, [warrantyService]);

  useEffect(() => {
    if (isDrawerOpen) {
      topOfComponent.current?.parentElement?.scrollTo({ top: 0 });
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    if (hasInteractedWithTerms.current && !checked) {
      handleScroll();
    }
  }, [checked]);

  const insuranceProductInformationDocument = (
    <a
      href={protectPlusScheme.insuranceProductInformationDocument}
      target="_blank"
      rel="noreferrer"
    >
      {"Insurance Product Information Document"}
    </a>
  );

  const termsAndConditions = (
    <a
      href={protectPlusScheme.termsAndConditions}
      target="_blank"
      rel="noreferrer"
    >
      {"Terms and Conditions"}
    </a>
  );

  const handleScroll = () => {
    // Wait two animation frames before measuring scrollHeight. The first frame
    // lets any pending state update (and its re-render) commit; the second
    // guarantees that the browser has completed paint for that new content.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollParent = topOfComponent.current?.parentElement;
        scrollParent?.scrollTo({
          top: scrollParent.scrollHeight,
          behavior: "smooth",
        });
      });
    });
  };

  const handleCheck = () => {
    hasInteractedWithTerms.current = true;
    setChecked((old) => !old);
    setDisableAddProtectPlusButton(checked);
    setTermsAgreed(!checked);
  };

  if (!warrantyService) {
    return null;
  }

  function handleQuantityChange(quantity: number) {
    if (!warrantyService) {
      return;
    }

    if (protectPlusAdded) {
      setIsLoading(true);

      handleStepperChange(warrantyService, quantity).then((isSuccess) => {
        if (isSuccess) {
          setServiceQuantity(quantity);
        }

        setIsLoading(false);
      });
    } else {
      setServiceQuantity(quantity);
    }
  }

  return (
    <div
      className={styles.protectPlusComplianceWrapper}
      data-testid="post-a2b-drawer-compliance"
      ref={topOfComponent}
    >
      <div className={styles.protectPlusCompliance}>
        <>
          <div className={styles.protectPlusCompliance__accidentalCover}>
            <div>
              Add {duration ? duration + " years" : ""} accidental damage cover?
            </div>
            <div
              className={
                styles.protectPlusCompliance__accidentalCover__standardGuarantee
              }
            >
              {marketingMessage}
            </div>
          </div>

          {warrantyService && (
            <div>
              <div className={styles.protectPlusCompliance__service}>
                <p
                  className={styles.protectPlusCompliance__service__title}
                  data-testid="service-label_compliance"
                >
                  <span>
                    <ProtectPlusSvg />
                  </span>
                  <span
                    className={
                      styles.protectPlusCompliance__service__title__price
                    }
                  >
                    {warrantyService.display}
                    {maxAllowedServices > 1 && !hideFooter && (
                      <span
                        className={
                          styles.protectPlusCompliance__service__title__per_policy
                        }
                      >
                        per policy
                      </span>
                    )}
                  </span>
                </p>
                {maxAllowedServices > 1 && !hideFooter && (
                  <Stepper
                    data-testid="compliance-protect-plus-stepper"
                    value={serviceQuantity}
                    id={"post-add-service-quantity"}
                    onChange={handleQuantityChange}
                    name={"quantity-selector"}
                    min={1}
                    max={maxAllowedServices}
                    labelText="Quantity"
                    showLabel={false}
                  />
                )}
              </div>
              <div className={styles.protectPlusCompliance__whatsCovered}>
                <div>
                  <Tick size="medium" />
                  {" What's covered"}
                </div>
                <ul>
                  {protectPlusScheme.whatsCovered?.map(
                    (whatsCovered, index) => (
                      <li key={`guarantee-${index}`}>
                        {whatsCovered.coverage}
                        {whatsCovered.details &&
                          whatsCovered.details.length > 0 && (
                            <ul className={styles.dash}>
                              {whatsCovered.details.map((detail) => (
                                <li key={detail}>{detail}</li>
                              ))}
                            </ul>
                          )}
                      </li>
                    ),
                  )}
                </ul>
              </div>

              <div className={styles.protectPlusCompliance__whatsNotCovered}>
                <div>
                  <Info size="medium" />
                  {"What's not covered"}
                </div>
                <ul>
                  {protectPlusScheme.whatsNotCovered?.map(
                    (whatsNotCovered, index) => (
                      <li key={`no-guarantee-${index}`}>
                        {whatsNotCovered.exclusion}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          )}
        </>
        <div></div>
        <div>
          <div
            data-testid="demands-and-needs"
            className={styles.protectPlusCompliance__demandsAndNeeds}
          >
            {protectPlusScheme.demandsAndNeedsStatement} See{" "}
            {insuranceProductInformationDocument}
            {"."}
            {" " + complaintDetails + " "} {termsAndConditions}
            <div data-testid="demands-and-needs-taxes">
              Price includes all applicable taxes.
            </div>
          </div>
        </div>

        <div className={styles.protectPlusCompliance__findOutMore}>
          <UnderlinedButton
            size="small"
            tag="a"
            href="https://www.johnlewis.com/our-services/protect-plus"
            target="_blank"
            labelText="Find out more about Protect Plus"
          />
          <span className={styles.visuallyHidden}>
            - this link will open in a new tab
          </span>
          <OpenInNewOutlined size="medium" />
        </div>

        <div className={styles.protectPlusCompliance__providedBy}>
          Provided By Domestic &amp; General Insurance PLC
        </div>

        <HorizontalRule />
        <div>
          {!protectPlusAdded && !hideFooter && (
            <>
              <div
                className={
                  termsAgreed
                    ? styles.protectPlusCompliance__checkbox
                    : styles.protectPlusCompliance__checkboxContainer
                }
              >
                <CheckboxWithContainer
                  id={"protect-plus-service-checkbox"}
                  name={"Protect Plus"}
                  checked={checked}
                  isControlled={true}
                  onClick={handleCheck}
                  disabled={isLoading}
                  additionalInfo={
                    !termsAgreed && (
                      <p
                        className={styles.errorLabel}
                        data-testid="error-message"
                      >
                        {
                          "Please confirm that you've read and understood the Information Document and Terms and Conditions"
                        }
                      </p>
                    )
                  }
                  label={
                    <p data-testid="service-label">
                      I can confirm I have read the{" "}
                      {insuranceProductInformationDocument} and{" "}
                      {termsAndConditions} and that this policy meets my demands
                      and needs
                    </p>
                  }
                  data-testid="post-a2b-drawer-protect-plus-compliance-checkbox"
                  ariaLabel="Terms and conditions"
                />
              </div>
              <div className={styles.scrollToBottom} ref={elementCheckBox}>
                Scroll To Bottom
              </div>
            </>
          )}
        </div>
      </div>
      {!hideFooter && (
        <div
          className={styles.protectPlusCompliance__footer}
          data-testid="post-a2b-drawer-compliance-footer"
        >
          {protectPlusAdded ? (
            <>
              <DEPRECATED_Message
                dataTestId="added-to-your-basket"
                title={
                  maxAllowedServices === 0
                    ? "Protect Plus selected"
                    : (serviceQuantity > 1 ? serviceQuantity + " x " : "") +
                      "Protect Plus added to your basket"
                }
                body={
                  maxAllowedServices === 0 ? (
                    <span>
                      Now add your item to basket and we&apos;ll include Protect
                      Plus with your order
                    </span>
                  ) : (
                    <span>
                      You have confirmed that you have read the{" "}
                      {insuranceProductInformationDocument} and{" "}
                      {termsAndConditions} and that this policy meets your
                      demands and needs
                    </span>
                  )
                }
                className={styles.protectPlusCompliance__footer__message}
                type="success"
              />

              <PrimaryButton
                className={styles.protectPlusCompliance__continueShoppingButton}
                onClick={closeDrawer}
                labelText="Continue Shopping"
                width="full-width"
              />
            </>
          ) : (
            <>
              {mode !== BasketMode.Post &&
                maxAllowedServices === 0 &&
                !protectPlusAdded && (
                  <DEPRECATED_Message
                    title={
                      "Protect Plus will be added once you have added your item to basket"
                    }
                    className={styles.protectPlusCompliance__footer__message}
                    type="information"
                  />
                )}
              <PrimaryButton
                className={styles.protectPlusCompliance__addServiceButton}
                disabled={isLoading}
                onClick={async () => {
                  if (disableAddProtectPlusButton) {
                    setTermsAgreed(false);
                    handleScroll();
                  } else {
                    setIsLoading(true);
                    const result = await addProtectPlusClicked(
                      warrantyService,
                      serviceQuantity,
                    );
                    if (result) {
                      setProtectPlusAdded(true);
                    }
                    setIsLoading(false);
                  }
                }}
                labelText="Add Protect Plus"
                width="full-width"
              />
              <SecondaryButton
                className={styles.protectPlusCompliance__skipServiceButton}
                onClick={closeDrawer}
                labelText="Continue without Protect Plus"
                width="full-width"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
